import { Component, OnDestroy, OnInit } from '@angular/core';
import { AttorneyService } from './service/attorney.service';
import { Attorney, GetItemsParams } from './service/attorney.model';
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs';
import { Page, MaxLength, ToastType } from 'src/app/enums/enums';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PhonenumberPipe } from 'src/app/pipes/phonenumber.pipe';
import { ToastService } from 'src/app/services/toast.service';
import { ApiService } from 'src/app/services/api.service';
import { SharedService } from 'src/app/services/shared.service';

@Component({
  selector: 'app-attorney',
  templateUrl: './attorney.component.html',
  styleUrls: ['./attorney.component.scss']
})
export class AttorneyComponent implements OnInit, OnDestroy {
  private EditAttorneyID: number | null = null;
  private Unsubscribe = new Subject<void>();
  private SearchSubject = new Subject<string>();
  public IsLoading = false;
  public AttorneyList: Attorney[] = [];
  public IsEditAttorney = false;
  public TotalAttorney = 0;
  public MaxPhoneLength = MaxLength.Phone;
  public MaxZipLength = MaxLength.Zip;
  public PaginationParams!: GetItemsParams;
  public SearchVal: string = '';

  AttorneyForm: FormGroup = new FormGroup({
    attorneyName: new FormControl('', [Validators.required]),
    address: new FormControl(''),
    town: new FormControl(''),
    zip: new FormControl('', [Validators.pattern('^[0-9]{5}$')]), // Example pattern for 5-digit zip
    phone: new FormControl(''), // Example for 10-digit phone
    fax: new FormControl(''), // Optional field
    contact: new FormControl('')
  });
  constructor(private phoneNumberPipe: PhonenumberPipe, private attorneyService: AttorneyService, private toast: ToastService, public apiService: ApiService, public sharedService: SharedService) {
    this.PaginationParams = {
      searchValue: '',
      skip: 0,
      take: Number(this.sharedService.getData('records')) || Page.Size,
      sortColumn: 'attorneyName',
      // clinicId: this.sharedService.CurrentClinic.value || 0,
      sortOrder: 'asc'
    };
  }

  ngOnInit(): void {
    this.GetAttorneyList(this.PaginationParams);

    this.SearchSubject.pipe(
      debounceTime(500), // Wait for 300ms pause in input
      distinctUntilChanged(), // Ignore if the search term is the same as previous
      switchMap((term) => {
        this.PaginationParams.searchValue = term;
        this.GetAttorneyList(this.PaginationParams);
        return '';
      })
    ).subscribe((results) => {
      console.log('results', results);
    });
  }

  ngOnDestroy(): void {
    this.Unsubscribe.next();
    this.Unsubscribe.complete();
  }

  OpenAddPopup() {
    this.IsEditAttorney = false;
    this.EditAttorneyID = null;
    this.AttorneyForm.reset();
  }
  OpenEditPopup(id: number) {
    this.IsEditAttorney = true;
    const res = this.attorneyService.GetAttorney(id);
    res.subscribe({
      next: (res: any) => {
        const { id, isDeleted, ...rest } = res.result;
        this.EditAttorneyID = id;
        this.AttorneyForm.setValue(rest);
      },
      complete: () => {}
    });
  }

  GetAttorneyList(params: GetItemsParams) {
    this.IsLoading = true;
    this.attorneyService.GetAttorneies(params).subscribe({
      next: (res) => {
        this.AttorneyList = [...res.result.data];
        this.AttorneyList = [...this.AttorneyList.filter((service) => !service.isDeleted)];
        this.TotalAttorney = res.result.count;
      },
      error: (err) => {
        this.IsLoading = false;
      },
      complete: () => (this.IsLoading = false)
    });
  }

  AddAttorney() {
    if (this.AttorneyForm.valid) {
      this.IsLoading = true;
      this.AttorneyForm.value.phone = this.phoneNumberPipe.transform(this.AttorneyForm.value.phone, 'unformat');
      this.AttorneyForm.value.fax = this.phoneNumberPipe.transform(this.AttorneyForm.value.fax, 'unformat');
      const response = this.attorneyService.CreateAttorney(this.AttorneyForm.value);
      response.subscribe({
        next: (res: any) => {
          this.toast.showToast({
            message: res.message,
            type: ToastType.Success
          });
          this.AttorneyForm.reset();
        },
        error: (err) => {
          this.IsLoading = false;
        },
        complete: () => this.GetAttorneyList(this.PaginationParams)
      });
    } else {
      console.log('Form is invalid');
    }
  }
  UpdateAttorney() {
    if (this.AttorneyForm.valid) {
      this.IsLoading = true;
      this.AttorneyForm.value.phone = this.phoneNumberPipe.transform(this.AttorneyForm.value.phone, 'unformat');
      this.AttorneyForm.value.fax = this.phoneNumberPipe.transform(this.AttorneyForm.value.fax, 'unformat');
      const requestBody = { id: this.EditAttorneyID, ...this.AttorneyForm.value };

      const response = this.attorneyService.UpdateAttorney(requestBody);
      response.subscribe({
        next: (res: any) => {
          this.toast.showToast({
            message: res.message,
            type: ToastType.Success
          });
          this.AttorneyForm.reset();
          this.EditAttorneyID = null;
        },
        error: (err) => {
          this.IsLoading = false;
        },
        complete: () => this.GetAttorneyList(this.PaginationParams)
      });
    } else {
      console.log('Form is invalid');
    }
  }
  DeleteAttorney() {
    if (this.EditAttorneyID) {
      this.IsLoading = true;
      this.attorneyService.DeleteAttorney(this.EditAttorneyID).subscribe({
        next: (res) => {
          this.toast.showToast({ message: res.message, type: ToastType.Success });
        },
        error: (err) => {
          this.IsLoading = false;
        },
        complete: () => this.GetAttorneyList(this.PaginationParams)
      });
    }
  }

  SortData(sortBy: string) {
    this.PaginationParams.sortColumn = sortBy;
    this.PaginationParams.sortOrder = this.PaginationParams.sortOrder === 'asc' ? 'desc' : 'asc';
    this.GetAttorneyList(this.PaginationParams);
  }

  SearchData() {
    this.SearchSubject.next(this.SearchVal);
  }

  /* Pagination */
  onPageChange(newPage: number): void {
    this.PaginationParams.skip = (newPage - 1) * this.PaginationParams.take;
    this.GetAttorneyList(this.PaginationParams);
  }
  onPageSizeChange(newPageSize: number): void {
    this.PaginationParams.take = newPageSize;
    this.PaginationParams.skip = 0;
    this.sharedService.setData('records', this.PaginationParams.take);
    this.GetAttorneyList(this.PaginationParams);
  }
}
